import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Teacher {
  id: string; full_name_en: string | null; full_name_ms: string | null;
  preferred_name: string | null; designation: string | null;
  email: string | null; isAvailable: boolean;
}

interface Period {
  id: string; day_of_week: number; period_number: number;
  start_time: string; end_time: string; period_type: string;
}

const DAYS_BN = ['Isnin','Selasa','Rabu','Khamis','Jumaat','Sabtu'];
const DAYS_EN = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const BN = {
  pageTitle: 'Penggantian Guru',
  pageSubtitle: 'cari guru tersedia untuk period tertentu',
  backHome: '← Laman Utama',
  backTimetable: '← Jadual Waktu',
  step1Title: 'Langkah 1 — Pilih Tarikh & Hari',
  step2Title: 'Langkah 2 — Pilih Period',
  step3Title: 'Langkah 3 — Pilih Guru Pengganti',
  step4Title: 'Langkah 4 — Tugaskan Pengganti',
  labelDate: 'Tarikh *',
  labelDay: 'Hari *',
  searchBtn: 'Cari Period',
  searchingBtn: 'Memuatkan…',
  labelSlotId: 'ID Slot Asal *',
  labelReason: 'Sebab',
  reasonPlaceholder: 'cth: Guru sakit',
  confirmBtn: '✓ Tugaskan Pengganti',
  confirmingBtn: 'Menugaskan…',
  availableStatus: 'Tersedia',
  busyStatus: 'Sibuk',
  emptyTeachers: 'Tiada guru aktif dijumpai untuk sekolah ini.',
  substituteTeacherLabel: 'Guru pengganti',
};

const EN = {
  pageTitle: 'Substitute Teacher Assignment',
  pageSubtitle: 'find available teachers for a specific period',
  backHome: '← Home',
  backTimetable: '← Timetable',
  step1Title: 'Step 1 — Select Date & Day',
  step2Title: 'Step 2 — Select Period',
  step3Title: 'Step 3 — Select Substitute Teacher',
  step4Title: 'Step 4 — Confirm Assignment',
  labelDate: 'Date *',
  labelDay: 'Day *',
  searchBtn: 'Find Periods',
  searchingBtn: 'Loading…',
  labelSlotId: 'Original Slot ID *',
  labelReason: 'Reason',
  reasonPlaceholder: 'e.g. Teacher sick',
  confirmBtn: '✓ Assign Substitute',
  confirmingBtn: 'Assigning…',
  availableStatus: 'Available',
  busyStatus: 'Busy',
  emptyTeachers: 'No active teachers found for this school.',
  substituteTeacherLabel: 'Substitute teacher',
};

const DEMO_SCHOOL_ID  = '3f868b12-b554-4f51-a893-69e9b4ba9f57';
const DEMO_VERSION_ID = '00000000-0000-0000-0000-000000000001';

export function SubstitutePage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  const DAYS = lang === 'en' ? DAYS_EN : DAYS_BN;

  const schoolId  = user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL_ID;

  const [date,         setDate]         = useState(() => new Date().toISOString().slice(0, 10));
  const [dayOfWeek,    setDayOfWeek]    = useState('1');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [reason,       setReason]       = useState('');

  const [periods,      setPeriods]      = useState<Period[]>([]);
  const [teachers,     setTeachers]     = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');

  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [assigning,    setAssigning]    = useState(false);

  const [periodsError, setPeriodsError] = useState<string | null>(null);
  const [teachersError, setTeachersError] = useState<string | null>(null);
  const [formError,    setFormError]    = useState<string | null>(null);
  const [toast,        setToast]        = useState<{ message: string; variant: ToastVariant } | null>(null);

  // Load periods on date/day change
  const loadPeriods = useCallback(async () => {
    setPeriodsLoading(true); setPeriodsError(null);
    try {
      const data = await apiGet<(Period & { is_active: boolean })[]>(`/api/timetable/periods/${schoolId}`);
      setPeriods(data.filter((p) => p.day_of_week === parseInt(dayOfWeek) && p.is_active));
    } catch (err) {
      setPeriodsError(err instanceof Error ? err.message : 'Ralat memuatkan period.');
    } finally { setPeriodsLoading(false); }
  }, [schoolId, dayOfWeek]);

  // Load available teachers when period selected
  const loadTeachers = useCallback(async (periodId: string) => {
    if (!periodId) return;
    setTeachersLoading(true); setTeachersError(null); setTeachers([]);
    try {
      const data = await apiGet<Teacher[]>(
        `/api/timetable/substitute/available?schoolId=${schoolId}&date=${date}&dayOfWeek=${dayOfWeek}&periodId=${periodId}&versionId=${DEMO_VERSION_ID}`,
      );
      setTeachers(data);
    } catch (err) {
      setTeachersError(err instanceof Error ? err.message : 'Ralat memuatkan senarai guru.');
    } finally { setTeachersLoading(false); }
  }, [schoolId, date, dayOfWeek]);

  function validateAndSearch() {
    if (!date) { setFormError('Sila pilih tarikh. / Select a date.'); return; }
    const today = new Date().toISOString().slice(0, 10);
    if (date < today) { setFormError('Tarikh tidak boleh dalam masa lalu. / Date cannot be in the past.'); return; }
    setFormError(null);
    setSelectedPeriod(''); setSelectedTeacher(''); setTeachers([]);
    void loadPeriods();
  }

  function handleSelectPeriod(periodId: string) {
    setSelectedPeriod(periodId);
    setSelectedTeacher('');
    void loadTeachers(periodId);
  }

  async function handleAssign() {
    if (!selectedSlot.trim()) { setFormError('Sila masukkan ID slot asal. / Enter original slot ID.'); return; }
    if (!selectedTeacher)     { setFormError('Sila pilih guru pengganti. / Select a substitute teacher.'); return; }
    if (!date)                { setFormError('Tarikh diperlukan. / Date required.'); return; }

    setAssigning(true); setFormError(null);
    try {
      await apiPost('/api/timetable/substitute', {
        originalSlotId:      selectedSlot.trim(),
        date,
        substituteTeacherId: selectedTeacher,
        reason:              reason.trim() || undefined,
      });
      setToast({ variant: 'success', message: 'Guru pengganti berjaya ditugaskan. / Substitute teacher assigned successfully.' });
      setSelectedSlot('');
      setSelectedTeacher('');
      setReason('');
      // Refresh available list
      void loadTeachers(selectedPeriod);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal menugaskan pengganti. / Failed to assign substitute.');
    } finally { setAssigning(false); }
  }

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backHome}</Link>
            <Link to="/admin/timetable" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none', marginLeft: spacing.md }}>{UI.backTimetable}</Link>
          </div>

          <h1 style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
            🔄 {UI.pageTitle}
          </h1>
          <p style={{ margin: `0 0 ${spacing.xl}`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
            {UI.pageSubtitle}
          </p>

          {/* Step 1: Date + Day */}
          <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg, boxShadow: shadows.xl }}>
            <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
              {UI.step1Title}
            </p>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelSt}>{UI.labelDate}</label>
                <input
                  type="date" value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => { setDate(e.target.value); setPeriods([]); setTeachers([]); setSelectedPeriod(''); }}
                  style={inputSt()}
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelSt}>{UI.labelDay}</label>
                <select value={dayOfWeek}
                  onChange={(e) => { setDayOfWeek(e.target.value); setPeriods([]); setTeachers([]); setSelectedPeriod(''); }}
                  style={inputSt()}>
                  {DAYS.map((d, i) => <option key={i+1} value={i+1}>{d}</option>)}
                </select>
              </div>
            </div>
            {formError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
            <Button variant="primary" size="sm" onClick={validateAndSearch} disabled={periodsLoading || !date}>
              {periodsLoading ? <><Spinner size="sm" />&nbsp;{UI.searchingBtn}</> : UI.searchBtn}
            </Button>
          </div>

          {/* Step 2: Period selection */}
          {periodsError && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{periodsError}</p>
            </div>
          )}

          {periods.length > 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
                {UI.step2Title}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                {periods.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPeriod(p.id)}
                    disabled={teachersLoading || assigning}
                    style={{
                      padding: `${spacing.xs} ${spacing.md}`,
                      backgroundColor: selectedPeriod === p.id ? colors.primary.yellow : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${selectedPeriod === p.id ? colors.primary.yellow : colors.neutral.gray800}`,
                      color: selectedPeriod === p.id ? colors.neutral.black : colors.neutral.white,
                      borderRadius: borderRadius.lg,
                      cursor: (teachersLoading || assigning) ? 'not-allowed' : 'pointer',
                      opacity: (teachersLoading || assigning) ? 0.6 : 1,
                      fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.sm,
                      fontWeight: selectedPeriod === p.id ? typography.fontWeight.bold : typography.fontWeight.medium,
                    }}
                  >
                    <span style={{ fontWeight: typography.fontWeight.bold }}>P{p.period_number}</span>
                    <br />
                    <span style={{ fontSize: typography.fontSize.xs }}>{p.start_time}–{p.end_time}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Available teachers */}
          {teachersError && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{teachersError}</p>
            </div>
          )}

          {teachersLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}>
              <Spinner size="lg" />
            </div>
          )}

          {!teachersLoading && selectedPeriod && teachers.length === 0 && !teachersError && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center', marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: colors.neutral.gray400 }}>
                {UI.emptyTeachers}
              </p>
            </div>
          )}

          {teachers.length > 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
                {UI.step3Title}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, maxHeight: '360px', overflowY: 'auto' }}>
                {teachers.map((t) => {
                  const name = t.preferred_name ?? t.full_name_ms ?? t.full_name_en ?? '—';
                  const isSelected = selectedTeacher === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => t.isAvailable && setSelectedTeacher(t.id)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: `${spacing.sm} ${spacing.md}`,
                        backgroundColor: isSelected ? 'rgba(247,208,0,0.1)' : t.isAvailable ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.2)',
                        border: `1px solid ${isSelected ? colors.primary.yellow : t.isAvailable ? colors.neutral.gray800 : 'transparent'}`,
                        borderRadius: borderRadius.lg,
                        cursor: t.isAvailable ? 'pointer' : 'not-allowed',
                        opacity: t.isAvailable ? 1 : 0.4,
                        gap: spacing.sm,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: isSelected ? colors.primary.yellow : colors.neutral.white }}>
                          {name}
                          {isSelected && ' ✓'}
                        </p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                          {t.designation ?? ''}
                          {t.email ? ` · ${t.email}` : ''}
                        </p>
                      </div>
                      <span style={{
                        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
                        color: t.isAvailable ? '#4ade80' : '#f87171',
                        backgroundColor: t.isAvailable ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                        padding: '2px 8px', borderRadius: borderRadius.full,
                        flexShrink: 0,
                      }}>
                        {t.isAvailable ? UI.availableStatus : UI.busyStatus}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Assign form */}
          {isAdmin && selectedTeacher && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg, border: `1px solid rgba(247,208,0,0.2)` }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
                {UI.step4Title}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '2 1 200px' }}>
                  <label style={labelSt}>{UI.labelSlotId}</label>
                  <input
                    type="text" value={selectedSlot}
                    onChange={(e) => setSelectedSlot(e.target.value)}
                    placeholder="UUID slot jadual"
                    disabled={assigning} style={inputSt()}
                  />
                </div>
                <div style={{ flex: '2 1 200px' }}>
                  <label style={labelSt}>{UI.labelReason}</label>
                  <input
                    type="text" value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={UI.reasonPlaceholder}
                    maxLength={500} disabled={assigning} style={inputSt()}
                  />
                </div>
              </div>
              <div style={{ padding: spacing.sm, backgroundColor: 'rgba(247,208,0,0.06)', borderRadius: borderRadius.md, marginBottom: spacing.md }}>
                <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.primary.yellow }}>
                  {UI.substituteTeacherLabel}: <strong>{teachers.find((t) => t.id === selectedTeacher)?.preferred_name ?? teachers.find((t) => t.id === selectedTeacher)?.full_name_en ?? '—'}</strong>
                  {' · '}{DAYS[parseInt(dayOfWeek) - 1]} {date}
                </p>
              </div>
              {formError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
              <Button
                variant="primary" size="md"
                onClick={() => void handleAssign()}
                disabled={assigning || !selectedSlot.trim()}
                loading={assigning}
              >
                {assigning ? UI.confirmingBtn : UI.confirmBtn}
              </Button>
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
